/**
 * MemberWallet
 *
 * The traveller's "boarding pass" view: member number, scannable barcode +
 * QR, current token balance, and embedded transaction history. Designed
 * to be opened on a phone at the bar/kiosk so staff can scan it.
 *
 * Data sources:
 *   - `getMyMemberIdentity` callable (memberNumber + signed barcode payload)
 *   - `regenerateMemberBarcode` callable (rotate after suspected leak)
 *   - Live `users/{uid}` snapshot for `tokens` balance
 *   - <TokenHistory userId={uid} embedded /> for the ledger
 *
 * Notes:
 *   - Code128 (jsbarcode) for laser scanners; QR (qrcode) for phone cameras.
 *   - Barcode payload is the HMAC-signed `<userId>.<memberNumber>.<v>.<sig>`
 *     produced server-side; we never re-sign client-side.
 *   - "Print Card" opens a stripped-down print view via window.print().
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIdBadge,
  faCoins,
  faSyncAlt,
  faPrint,
  faExclamationTriangle,
  faSpinner,
  faShieldAlt,
  faStore,
} from '@fortawesome/free-solid-svg-icons';

import { auth, firestore, functions } from '../FirebaseConfig';
import { useAuth } from '../contexts/CombinedAuthContext';
import { useToast } from './ui/Toast';
import { ConfirmDialog } from './ui/ConfirmDialog';
import TokenHistory from './TokenHistory';
import '../styles/MemberWallet.css';

function MemberWallet() {
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();

  const [identity, setIdentity] = useState(null); // {memberNumber, memberBarcodePayload}
  const [loadingIdentity, setLoadingIdentity] = useState(true);
  const [identityError, setIdentityError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [tokens, setTokens] = useState(0);

  const barcodeRef = useRef(null);
  const qrRef = useRef(null);

  // ------------------------------------------------------------------
  // Load identity from server
  // ------------------------------------------------------------------
  const loadIdentity = useCallback(async () => {
    if (!isAuthenticated) {
      setLoadingIdentity(false);
      return;
    }
    setLoadingIdentity(true);
    setIdentityError(null);
    try {
      const fn = httpsCallable(functions, 'getMyMemberIdentity');
      const res = await fn({});
      const data = res.data || {};
      if (!data.memberNumber || !data.memberBarcodePayload) {
        throw new Error('Server did not return a member identity.');
      }
      setIdentity({
        memberNumber: data.memberNumber,
        memberBarcodePayload: data.memberBarcodePayload,
      });
    } catch (err) {
      console.error('[MemberWallet] getMyMemberIdentity failed', err);
      setIdentityError(err.message || 'Could not load your member identity.');
    } finally {
      setLoadingIdentity(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { loadIdentity(); }, [loadIdentity]);

  // ------------------------------------------------------------------
  // Live token balance
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const fbUser = auth.currentUser;
    if (!fbUser) return undefined;
    const userRef = doc(firestore, 'users', fbUser.uid);
    const unsub = onSnapshot(userRef, (snap) => {
      const data = snap.data() || {};
      setTokens(typeof data.tokenBalance === 'number' ? data.tokenBalance : 0);
    });
    return () => unsub();
  }, [isAuthenticated]);

  // ------------------------------------------------------------------
  // Render barcode + QR whenever identity changes
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!identity || !identity.memberBarcodePayload) return;
    const payload = identity.memberBarcodePayload;

    // Code128 barcode
    if (barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, payload, {
          format: 'CODE128',
          width: 2,
          height: 80,
          displayValue: false,
          margin: 0,
          background: '#ffffff',
          lineColor: '#0a0a0a',
        });
      } catch (err) {
        console.error('[MemberWallet] JsBarcode failed', err);
      }
    }

    // QR code
    if (qrRef.current) {
      QRCode.toCanvas(qrRef.current, payload, {
        width: 200,
        margin: 1,
        color: { dark: '#0a0a0a', light: '#ffffff' },
        errorCorrectionLevel: 'M',
      }).catch((err) => console.error('[MemberWallet] QRCode failed', err));
    }
  }, [identity]);

  // ------------------------------------------------------------------
  // Regenerate barcode (invalidates the prior payload server-side)
  // ------------------------------------------------------------------
  const handleRegenerate = async () => {
    setConfirmRegen(false);
    setRegenerating(true);
    try {
      const fn = httpsCallable(functions, 'regenerateMemberBarcode');
      const res = await fn({});
      const data = res.data || {};
      if (!data.memberNumber || !data.payload) {
        throw new Error('Server did not return a refreshed barcode.');
      }
      setIdentity({
        memberNumber: data.memberNumber,
        memberBarcodePayload: data.payload,
      });
      showToast('A fresh barcode was issued. Old codes will no longer scan.', 'success');
    } catch (err) {
      console.error('[MemberWallet] regenerate failed', err);
      showToast(err.message || 'Could not regenerate barcode. Try again.', 'error');
    } finally {
      setRegenerating(false);
    }
  };

  // ------------------------------------------------------------------
  // Print card — uses a CSS @media print stylesheet on the wallet card
  // ------------------------------------------------------------------
  const handlePrint = () => { window.print(); };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (loadingIdentity) {
    return (
      <div className="member-wallet member-wallet--loading">
        <FontAwesomeIcon icon={faSpinner} spin />{' '}
        Loading your boarding pass…
      </div>
    );
  }

  if (identityError) {
    return (
      <div className="member-wallet member-wallet--error">
        <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
        {identityError}
        <div style={{ marginTop: 12 }}>
          <button className="r5-pack-cta" onClick={loadIdentity}>
            <FontAwesomeIcon icon={faSyncAlt} /> Retry
          </button>
        </div>
      </div>
    );
  }

  if (!identity) {
    return (
      <div className="member-wallet member-wallet--error">
        No member identity available. If you just signed up, please refresh in a moment.
      </div>
    );
  }

  const fbUser = auth.currentUser;
  const userId = fbUser ? fbUser.uid : null;

  return (
    <div className="member-wallet">
      {/* ---- Boarding-pass card (printable) ---- */}
      <div className="member-wallet__card" id="member-wallet-printable">
        <div className="mw-card__head">
          <FontAwesomeIcon icon={faIdBadge} />
          <span>The Pirate's Guild · Member Pass</span>
        </div>

        <div className="mw-card__body">
          <div className="mw-card__id-block">
            <div className="mw-card__label">Member #</div>
            <div className="mw-card__number">{identity.memberNumber}</div>
            <div className="mw-card__balance">
              <FontAwesomeIcon icon={faCoins} />
              <strong>{tokens}</strong> Doubloons aboard
            </div>
          </div>

          <div className="mw-card__codes">
            <div className="mw-card__qr-wrap">
              <canvas ref={qrRef} className="mw-card__qr" />
              <div className="mw-card__codelabel">Scan with phone</div>
            </div>
            <div className="mw-card__bar-wrap">
              <svg ref={barcodeRef} className="mw-card__barcode" />
              <div className="mw-card__codelabel">Code128 · for laser scanners</div>
            </div>
          </div>
        </div>

        <div className="mw-card__foot">
          Present this pass at any participating berth to spend Doubloons or claim member deals.
        </div>
      </div>

      {/* ---- Actions (hidden from print) ---- */}
      <div className="member-wallet__actions no-print">
        <button
          type="button"
          className="r5-pack-cta r5-pack-cta--bright"
          onClick={handlePrint}
        >
          <FontAwesomeIcon icon={faPrint} /> Print card
        </button>
        <button
          type="button"
          className="r5-pack-cta"
          onClick={() => setConfirmRegen(true)}
          disabled={regenerating}
          title="If your barcode was photographed or shared, rotate it now to invalidate the old one."
        >
          {regenerating
            ? <><FontAwesomeIcon icon={faSpinner} spin /> Rotating…</>
            : <><FontAwesomeIcon icon={faSyncAlt} /> Rotate barcode</>}
        </button>
      </div>

      <p className="member-wallet__hint no-print">
        <FontAwesomeIcon icon={faShieldAlt} />{' '}
        Your barcode is signed and tied to your account. Rotating it
        invalidates any old screenshots or printouts immediately.
      </p>

      <section className="member-wallet__topup no-print" aria-label="Doubloon top-up options">
        <h3 className="member-wallet__topup-title">
          <FontAwesomeIcon icon={faStore} /> Keep your wallet ready
        </h3>
        <p className="member-wallet__topup-copy">
          Top up before you head out so you can redeem immediately at participating businesses.
        </p>
        <ul className="member-wallet__topup-list">
          <li>Use your pass for faster checkout at redemption time.</li>
          <li>Stay ready for limited-time offers and event-night perks.</li>
          <li>Doubloons remain in your wallet for future participating offers.</li>
        </ul>
        <div className="member-wallet__topup-actions">
          <Link className="r5-pack-cta r5-pack-cta--bright" to="/tokens?src=wallet&intent=redeem">
            Top up for next redemption
          </Link>
          <Link className="r5-pack-cta" to="/tokens?src=wallet&intent=compare">
            Compare Doubloon packs
          </Link>
        </div>
      </section>

      {/* ---- Embedded ledger ---- */}
      {userId && (
        <div className="member-wallet__history no-print">
          <h3 className="member-wallet__history-title">
            <FontAwesomeIcon icon={faCoins} /> Doubloon activity
          </h3>
          <TokenHistory userId={userId} embedded maxItems={20} />
        </div>
      )}

      {/* ---- Confirm rotate ---- */}
      <ConfirmDialog
        isOpen={confirmRegen}
        title="Rotate your barcode?"
        message={
          <>
            This will invalidate your current barcode immediately. Anyone holding
            a screenshot or printout of the old code will no longer be able to scan it.
            Your member number ({identity.memberNumber}) will not change.
          </>
        }
        confirmText="Rotate barcode"
        cancelText="Keep current"
        onConfirm={handleRegenerate}
        onClose={() => setConfirmRegen(false)}
        type="warning"
      />
    </div>
  );
}

export default MemberWallet;
